import { motion } from "framer-motion";
import { Fern } from "@/components/site/BrandMark";
import { CalendarClock, Clock } from "lucide-react";
import { renderScriptTitle } from "@/components/site/scriptTitle";

const WEEKDAYS = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];
const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

const DEFAULT_TITLE = "Horários *disponíveis*";
const DEFAULT_INTRO = "Estes são os próximos horários livres para atendimento online. Escolha o que combina com você e envie sua mensagem para reservar.";

function label(dateStr) {
  try {
    const [y, m, d] = dateStr.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return { day: String(d).padStart(2, "0"), month: MONTHS[m - 1], wd: WEEKDAYS[dt.getDay()] };
  } catch {
    return { day: dateStr, month: "", wd: "" };
  }
}

export default function AvailabilityPublic({ content, slots = [], onPick }) {
  const grouped = slots.reduce((acc, s) => {
    (acc[s.date] = acc[s.date] || []).push(s.time);
    return acc;
  }, {});
  const dates = Object.keys(grouped).sort().slice(0, 6);

  return (
    <section id="horarios" className="relative bg-[#FDFBF7] py-24 sm:py-32 overflow-hidden" data-testid="availability-public">
      <Fern color="#5B6E5B" opacity={0.08} className="pointer-events-none absolute -right-12 top-10 w-72 scale-x-[-1]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="max-w-2xl">
          <span className="font-body text-xs tracking-[0.4em] uppercase text-[#C87A5E]">Agenda</span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl text-[#2C332A] leading-tight text-balance">
            {renderScriptTitle(content?.availability_title || DEFAULT_TITLE, "font-script text-[#5B6E5B] text-5xl sm:text-6xl")}
          </h2>
          <p className="mt-5 font-body text-[#4A5D4E] leading-relaxed">
            {content?.availability_intro || DEFAULT_INTRO}
          </p>
        </motion.div>

        {dates.length === 0 ? (
          <div className="mt-12 flex items-center gap-3 rounded-2xl bg-[#F5F0E6] border border-[#5B6E5B]/10 p-8 max-w-xl">
            <CalendarClock className="w-6 h-6 text-[#5B6E5B]" strokeWidth={1.4} />
            <p className="font-body text-[#4A5D4E]">No momento não há horários publicados. Envie uma mensagem que combinamos o melhor dia para você. 🌿</p>
          </div>
        ) : (
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {dates.map((d, i) => {
              const l = label(d);
              return (
                <motion.div
                  key={d}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.06 }}
                  className="rounded-2xl bg-white border border-[#5B6E5B]/10 shadow-[0_8px_32px_rgba(91,110,91,0.06)] p-6"
                  data-testid="public-slot-day"
                >
                  <div className="flex items-baseline gap-3">
                    <span className="font-display text-4xl text-[#5B6E5B]">{l.day}</span>
                    <div className="leading-tight">
                      <p className="font-body text-sm text-[#2C332A] capitalize">{l.month}</p>
                      <p className="font-body text-xs text-[#7A8A7A] capitalize">{l.wd}</p>
                    </div>
                  </div>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {grouped[d].sort().map((t) => (
                      <button
                        key={t}
                        onClick={() => onPick?.(d, t)}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#F5F0E6] px-3.5 py-1.5 font-body text-sm text-[#2C332A] transition-colors duration-300 hover:bg-[#5B6E5B] hover:text-[#FDFBF7]"
                        data-testid="public-slot-time"
                      >
                        <Clock className="w-3.5 h-3.5" strokeWidth={1.6} /> {t}
                      </button>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
